import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router";
import { ApiError } from "./api/client";
import { keys } from "./api/queries";
import { AuthProvider } from "./auth/AuthContext";
import { AppLayout } from "./components/Layout";
import { ArenaMatchPage } from "./routes/ArenaMatchPage";
import { ArenaPage } from "./routes/ArenaPage";
import { AuditPage, NotFoundPage } from "./routes/AuditPage";
import { BadgesPage } from "./routes/BadgesPage";
import { BannersPage } from "./routes/BannersPage";
import { BrainPage } from "./routes/BrainPage";
import { ConfigPage } from "./routes/ConfigPage";
import { DashboardPage } from "./routes/DashboardPage";
import { DivisionsPage } from "./routes/DivisionsPage";
import { DuelDetailPage, DuelsPage } from "./routes/DuelsPage";
import { IntegrityPage } from "./routes/IntegrityPage";
import { JobsPage } from "./routes/JobsPage";
import { LeaderboardsPage } from "./routes/LeaderboardsPage";
import { LearnPage } from "./routes/LearnPage";
import { StoryEditorPage } from "./routes/learn/StoryEditorPage";
import { LevelsPage } from "./routes/LevelsPage";
import { LoginPage } from "./routes/LoginPage";
import { MonetisationPage } from "./routes/MonetisationPage";
import { NotificationsPage } from "./routes/NotificationsPage";
import { AdsPage } from "./routes/AdsPage";
import { AskLumePage } from "./routes/AskLumePage";
import { AutomationPage } from "./routes/AutomationPage";
import { EngagementPage } from "./routes/EngagementPage";
import { PackPage } from "./routes/PackPage";
import { DraftPackPage } from "./routes/levelpacks/DraftPackPage";
import { PlayerDetailPage, PlayersPage } from "./routes/PlayersPage";
import { PuzzleDetailPage } from "./routes/PuzzleDetailPage";
import { PuzzlesPage } from "./routes/PuzzlesPage";
import { ReportsPage } from "./routes/ReportsPage";
import { RoomDetailPage, RoomsPage } from "./routes/RoomsPage";
import { SchedulePage } from "./routes/SchedulePage";
import { SupportPage } from "./routes/SupportPage";
import { StudioBankPage } from "./routes/studio/StudioBankPage";
import { StudioHomePage } from "./routes/studio/StudioHomePage";
import { StudioJobPage } from "./routes/studio/StudioJobPage";
import { StudioJobsPage } from "./routes/studio/StudioJobsPage";
import { StudioNewJobPage } from "./routes/studio/StudioNewJobPage";
import { TeamDetailPage, TeamsPage } from "./routes/TeamsPage";
import { AppUpdatesPage } from "./routes/AppUpdatesPage";
import { ExperimentsPage } from "./routes/paywall/ExperimentsPage";
import { PaywallSettingsPage } from "./routes/paywall/PaywallSettingsPage";
import { WebPaymentsPage } from "./routes/payments/WebPaymentsPage";
import { CustomersPage } from "./routes/revenue/CustomersPage";
import { EventsPage } from "./routes/revenue/EventsPage";
import { PlayerSubscriptionPage } from "./routes/revenue/PlayerSubscriptionPage";
import { RevenuePage } from "./routes/revenue/RevenuePage";
import { CupDetailPage } from "./routes/tournaments/CupDetailPage";
import { CupEditorPage } from "./routes/tournaments/CupEditorPage";
import { CupPuzzlesPage } from "./routes/tournaments/CupPuzzlesPage";
import { CupSetPage } from "./routes/tournaments/CupSetPage";
import { CupSettingsPage } from "./routes/tournaments/CupSettingsPage";
import { CupsPage } from "./routes/tournaments/CupsPage";
import { PrizesPage } from "./routes/tournaments/PrizesPage";
import { ShareDetailPage } from "./routes/sharing/ShareDetailPage";
import { SharingPage } from "./routes/sharing/SharingPage";
import { SharingSettingsPage } from "./routes/sharing/SharingSettingsPage";

export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "puzzles", element: <PuzzlesPage /> },
      { path: "puzzles/:id", element: <PuzzleDetailPage /> },
      { path: "schedule", element: <SchedulePage /> },
      // Puzzle Studio (contracts/studio.md §4)
      { path: "studio", element: <StudioHomePage /> },
      { path: "studio/new", element: <StudioNewJobPage /> },
      { path: "studio/jobs", element: <StudioJobsPage /> },
      { path: "studio/jobs/:id", element: <StudioJobPage /> },
      { path: "studio/bank", element: <StudioBankPage /> },
      { path: "levels", element: <LevelsPage /> },
      { path: "levels/:track/:index", element: <PackPage /> },
      // Studio level packs (contracts/studio.md §5): a draft under review
      { path: "levels/:track/:index/draft", element: <DraftPackPage /> },
      { path: "players", element: <PlayersPage /> },
      { path: "players/:id", element: <PlayerDetailPage /> },
      // A player's subscriptions and journey, a tab next to the profile (contracts/revenue.md §5)
      { path: "players/:id/subscription", element: <PlayerSubscriptionPage /> },
      { path: "teams", element: <TeamsPage /> },
      { path: "teams/:id", element: <TeamDetailPage /> },
      { path: "divisions", element: <DivisionsPage /> },
      { path: "arena", element: <ArenaPage /> },
      { path: "arena/matches/:id", element: <ArenaMatchPage /> },
      { path: "duels", element: <DuelsPage /> },
      { path: "duels/:id", element: <DuelDetailPage /> },
      { path: "rooms", element: <RoomsPage /> },
      { path: "rooms/:id", element: <RoomDetailPage /> },
      // Tournaments (contracts/tournaments.md §9): cups, their puzzle pools and sets, the prize queue, the cups.* settings
      { path: "tournaments", element: <CupsPage /> },
      { path: "tournaments/new", element: <CupEditorPage /> },
      { path: "tournaments/puzzles", element: <CupPuzzlesPage /> },
      { path: "tournaments/puzzles/:setId", element: <CupSetPage /> },
      { path: "tournaments/prizes", element: <PrizesPage /> },
      { path: "tournaments/settings", element: <CupSettingsPage /> },
      { path: "tournaments/:id", element: <CupDetailPage /> },
      { path: "tournaments/:id/edit", element: <CupEditorPage /> },
      { path: "notifications", element: <NotificationsPage /> },
      // Messaging (contracts/messaging.md): banners and the support chat (one route, so the list keeps its filter)
      { path: "banners", element: <BannersPage /> },
      { path: "support/:userId?", element: <SupportPage /> },
      // Learn (contracts/learn.md): stories and the daily brain card
      { path: "learn", element: <LearnPage /> },
      { path: "learn/new", element: <StoryEditorPage /> },
      { path: "learn/:id", element: <StoryEditorPage /> },
      { path: "brain", element: <BrainPage /> },
      // Lume (contracts/lume.md): the line library, Ask Lume, the engagement report
      { path: "automation", element: <AutomationPage /> },
      { path: "ask-lume", element: <AskLumePage /> },
      { path: "engagement", element: <EngagementPage /> },
      // Sharing (contracts/index-cards.md §5): shares, link clicks, app opens, the index population; the index.* / share.* settings
      { path: "sharing", element: <SharingPage /> },
      { path: "sharing/settings", element: <SharingSettingsPage /> },
      { path: "sharing/shares/:kind/:ref", element: <ShareDetailPage /> },
      { path: "reports", element: <ReportsPage /> },
      { path: "leaderboards", element: <LeaderboardsPage /> },
      { path: "integrity", element: <IntegrityPage /> },
      { path: "badges", element: <BadgesPage /> },
      { path: "monetisation", element: <MonetisationPage /> },
      // Revenue, customers (subscriptions) and the store event log, RevenueCat-style (contracts/revenue.md §5)
      { path: "monetisation/revenue", element: <RevenuePage /> },
      { path: "monetisation/customers", element: <CustomersPage /> },
      { path: "monetisation/events", element: <EventsPage /> },
      // Ads for free players (contracts/ads.md §5)
      { path: "monetisation/ads", element: <AdsPage /> },
      // Paywall settings and experiments (contracts/paywall.md §3, §5)
      { path: "monetisation/paywall", element: <PaywallSettingsPage /> },
      { path: "monetisation/experiments", element: <ExperimentsPage /> },
      // Pro on the web through Stripe: connection, settings, packages, transactions, webhook events (contracts/web.md §5.7)
      { path: "monetisation/web-payments", element: <WebPaymentsPage /> },
      // App versions, release notes and the rating prompt (contracts/app-updates.md)
      { path: "app-updates", element: <AppUpdatesPage /> },
      { path: "jobs", element: <JobsPage /> },
      { path: "config", element: <ConfigPage /> },
      { path: "audit", element: <AuditPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export function createQueryClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      // A 401 anywhere means the session cookie expired: re-check /me, which sends us to /login.
      onError: (error, query) => {
        if (error instanceof ApiError && error.status === 401 && query.queryKey[0] !== keys.me[0]) {
          client.setQueryData(keys.me, null);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401) client.setQueryData(keys.me, null);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      },
    },
  });
  return client;
}

export function Providers({ client, children }: { client: QueryClient; children: React.ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}

export function App() {
  const [client] = useState(createQueryClient);
  const [router] = useState(() => createBrowserRouter(routes));
  return (
    <Providers client={client}>
      <RouterProvider router={router} />
    </Providers>
  );
}
